// Matches a product's admin-typed printMethod free-text value (see
// ProductConfig.printMethods's own doc comment — there is no fixed
// PrintMethod enum in this codebase) against the set of real-world labels
// vendors/admins have used historically for embroidery methods, so the
// embroidery-preview module (services/embroideryPreviewService.ts) can
// recognize a design that should get the real digitized preview.
//
// Case-insensitive substring match, not exact-equality — confirmed real
// values across vendors include "Embroidery", "Borduren" (Dutch),
// "3D borduren", "3D Embroidery" (see translationLookup.ts's own vendor
// method-name tables) and there is no guarantee an admin won't type a
// product-specific variant like "Borduren (voorzijde)". A substring match
// on these known roots covers all observed real values without needing an
// exhaustive enum an admin would have to keep in sync by hand.
const EMBROIDERY_METHOD_KEYWORDS = ['embroidery', 'borduren', 'borduring'];

export function isEmbroideryPrintMethod(printMethod: string | null | undefined): boolean {
  if (!printMethod) return false;
  const lower = printMethod.toLowerCase();
  return EMBROIDERY_METHOD_KEYWORDS.some(kw => lower.includes(kw));
}

// Puff/3D embroidery specifically (satin_column + emboss lighting) vs. flat
// embroidery (auto_fill, no lighting pass) — see embroideryPreviewService.ts.
// "3D" or "puff" anywhere in the label is the real-world signal admins use
// (confirmed pattern from translationLookup.ts's "3D borduren"/"3D
// Embroidery" vendor values); anything else matching
// isEmbroideryPrintMethod() but not this falls back to flat.
export function isPuffEmbroideryPrintMethod(printMethod: string | null | undefined): boolean {
  if (!printMethod) return false;
  const lower = printMethod.toLowerCase();
  return isEmbroideryPrintMethod(printMethod) && (lower.includes('3d') || lower.includes('puff'));
}

export const EMBROIDERY_PREVIEW_DESIGN_IMAGE_TYPE = 'embroidery_preview';

// Design.embroideryPreviewStatus int convention — mirrors OrderDesign.status
// (see schema.prisma's own comment on the column).
export const EmbroideryPreviewStatus = {
  NONE:       0,
  QUEUED:     1,
  PROCESSING: 2,
  DONE:       3,
  FAILED:     4,
} as const;
export type EmbroideryPreviewStatusValue = typeof EmbroideryPreviewStatus[keyof typeof EmbroideryPreviewStatus];
